import React from "react";
import { Box, Grid, Typography, Card, CardContent } from "../components/primitives";

/**
 * Renders a responsive card layout for data rows.
 * This utility function transforms table data into an accessible card-based layout
 * for mobile/small screen views, ensuring WCAG 1.4.10 Reflow compliance.
 * 
 * @param {Object} config - Configuration object
 * @param {Array} config.data - Array of data rows to display
 * @param {Array} config.columns - Column definitions from the table
 * @param {Object} config.cardConfig - Card configuration object
 * @param {Array} config.cardConfig.fields - Field definitions for card display
 * @param {string} config.cardConfig.fields[].field - Field name from row data
 * @param {string} config.cardConfig.fields[].label - Display label (can be translation key)
 * @param {boolean} config.cardConfig.fields[].primary - Whether this is a primary/highlighted field
 * @param {Function} config.cardConfig.fields[].renderCell - Optional custom renderer
 * @param {Function} config.cardConfig.fields[].renderValue - Optional value transformer
 * @param {boolean} config.cardConfig.fields[].hidden - Whether to hide this field
 * @param {string} config.cardConfig.keyField - Field to use as unique key (default: 'id')
 * @param {string} config.cardConfig.ariaLabel - Aria label for the list
 * @param {Function} config.cardConfig.onCardClick - Optional click handler for cards
 * @param {Object} config.cardConfig.cardStyles - Optional custom styles for cards
 * @param {boolean} config.initialLoading - Loading state
 * @param {Function} config.t - Translation function
 * @param {string} config.noDataMessage - Message to show when no data
 * @returns {JSX.Element} Rendered card layout
 */
export const renderCardLayout = ({
  data,
  columns,
  cardConfig,
  initialLoading = false,
  t = (key, fallback) => fallback || key,
  noDataMessage,
  ...boxProps
}) => {
  if (!cardConfig || !cardConfig.fields) {
    console.error('cardLayoutRenderer: cardConfig.fields is required');
    return null;
  }

  const {
    fields,
    keyField = 'id',
    ariaLabel = 'Data list',
    onCardClick,
    cardStyles = {},
    gridItemProps = {},
  } = cardConfig;

  // Default styles with customization support
  const defaultCardStyles = {
    mb: 2,
    border: '1px solid #e0e0e0',
    '&:hover': {
      boxShadow: 2,
      cursor: onCardClick ? 'pointer' : 'default',
    },
    ...cardStyles,
  };

  const renderFieldValue = (row, fieldConfig, columnDef) => {
    const { field, renderCell, renderValue, primary = false } = fieldConfig;
    const value = row[field];

    // If custom renderCell is provided (from column definition or field config)
    if (renderCell && typeof renderCell === 'function') {
      // Pass params in the same format as DataGrid to ensure compatibility
      return renderCell({ value, row, field, getValue: (f) => row[f] });
    }

    // If custom renderValue is provided
    if (renderValue && typeof renderValue === 'function') {
      return (
        <Typography 
          variant={primary ? "h6" : "body1"} 
          sx={{ fontWeight: primary ? 500 : 400 }}
        >
          {renderValue(value, row)}
        </Typography>
      );
    }

    // Default rendering
    return (
      <Typography 
        variant={primary ? "h6" : "body1"}
        sx={{ fontWeight: primary ? 500 : 400 }}
      >
        {value !== null && value !== undefined ? value.toString() : 'N/A'}
      </Typography>
    );
  };

  const getFieldLabel = (fieldConfig) => {
    const { label } = fieldConfig;
    // If label looks like a translation key, use translation function
    if (typeof label === 'string' && label.includes('.')) {
      return t(label, label);
    }
    return label;
  };

  return (
    <Box
      sx={{ width: '100%' }}
      role="list"
      aria-label={ariaLabel}
      aria-busy={initialLoading}
      {...boxProps}
    >
      {data && data.length > 0 ? (
        data.map((row, index) => {
          const key = row[keyField] || index;
          
          return (
            <Card
              key={key}
              role="listitem"
              sx={defaultCardStyles}
              onClick={onCardClick ? () => onCardClick(row, index) : undefined}
              tabIndex={onCardClick ? 0 : undefined}
              onKeyPress={
                onCardClick
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onCardClick(row, index);
                      }
                    }
                  : undefined
              }
            >
              <CardContent>
                <Grid container spacing={2}>
                  {fields.map((fieldConfig, fieldIndex) => {
                    // Skip hidden fields
                    if (fieldConfig.hidden) return null;

                    const columnDef = columns.find(col => col.field === fieldConfig.field);
                    const fieldLabel = getFieldLabel(fieldConfig);

                    return (
                      <Grid 
                        item 
                        xs={12} 
                        key={`${key}-${fieldConfig.field}-${fieldIndex}`}
                        {...gridItemProps}
                      >
                        {/* Field Label */}
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          gutterBottom
                          component="dt"
                        >
                          {fieldLabel}
                        </Typography>
                        
                        {/* Field Value */}
                        <Box component="dd" sx={{ m: 0 }}>
                          {renderFieldValue(row, fieldConfig, columnDef)}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          );
        })
      ) : (
        !initialLoading && (
          <Typography 
            variant="body1" 
            sx={{ mt: 2, textAlign: 'center' }}
            role="status"
          >
            {noDataMessage || t("erps.common.noRecordsFound", "No records found")}
          </Typography>
        )
      )}
    </Box>
  );
};

/**
 * Creates a card configuration from column definitions.
 * This helper automatically generates a cardConfig object from table column definitions.
 * 
 * @param {Array} columns - Column definitions from the table
 * @param {Object} options - Configuration options
 * @param {string} options.keyField - Field to use as unique key
 * @param {string} options.primaryField - Field to mark as primary
 * @param {Array} options.excludeFields - Fields to exclude from card display
 * @param {Object} options.fieldOverrides - Override specific field configurations
 * @returns {Object} Card configuration object
 */
export const createCardConfigFromColumns = (
  columns,
  options = {}
) => {
  const {
    keyField = 'id',
    primaryField = null,
    excludeFields = [],
    fieldOverrides = {},
    ariaLabel = 'Data list',
  } = options;

  const fields = columns
    .filter(col => col.field && col.headerName && !excludeFields.includes(col.field))
    .map((col, index) => {
      const isPrimary = primaryField ? col.field === primaryField : index === 0;
      
      return {
        field: col.field,
        label: col.headerName,
        primary: isPrimary,
        renderCell: col.renderCell,
        ...fieldOverrides[col.field], // Allow field-specific overrides
      };
    });

  return {
    fields,
    keyField,
    ariaLabel,
  };
};

/**
 * Higher-order function to create a custom card renderer with preset configuration.
 * Useful for creating reusable card renderers with specific styling or behavior.
 * 
 * @param {Object} defaultConfig - Default configuration to merge with runtime config
 * @returns {Function} Card renderer function
 */
export const createCardRenderer = (defaultConfig = {}) => {
  return (config) => renderCardLayout({ ...defaultConfig, ...config });
};

